(() => {
  if (customElements.get('a2tv-home-showcase')) return;
  customElements.define('a2tv-home-showcase', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (!this.slides.length) return;
      this.selectors = [...this.querySelectorAll('[data-select]')];
      this.videos = this.slides.map(slide => slide.querySelector('video'));
      this.videoToggle = this.querySelector('[data-video-toggle]');
      this.rotationToggle = this.querySelector('[data-rotation-toggle]');
      this.status = this.querySelector('[data-status]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.reduced = this.motion.matches;
      this.saveData = !!navigator.connection?.saveData;
      this.index = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
      this.inView = false;
      this.hovering = false;
      this.focused = false;
      this.auto = this.dataset.autoplay === 'true' && !this.reduced && !this.saveData;
      this.videoPaused = this.reduced || this.saveData;
      this.elapsed = 0;
      this.interval = Math.max(6000, Number(this.dataset.interval) || 10000);
      this.abort = new AbortController();
      const on = (element, event, fn) => element.addEventListener(event, fn, {signal:this.abort.signal});
      this.selectors.forEach((button, index) => on(button, 'click', () => this.select(index, true)));
      on(this.rotationToggle, 'click', () => {
        this.auto = !this.auto;
        this.elapsed = 0;
        // Explicitly starting rotation is allowed while this control has focus.
        this.rotationFocusAllowed = this.auto;
        this.sync();
      });
      on(this.videoToggle, 'click', () => {
        this.videoPaused = !this.videos[this.index].paused;
        this.sync();
      });
      on(this, 'pointerenter', event => { if (event.pointerType === 'mouse') this.hovering = true; });
      on(this, 'pointerleave', () => { this.hovering = false; });
      on(this, 'focusin', event => {
        this.focused = true;
        if (event.target !== this.rotationToggle) this.rotationFocusAllowed = false;
      });
      on(this, 'focusout', () => queueMicrotask(() => {
        this.focused = this.contains(document.activeElement);
        if (!this.focused) this.rotationFocusAllowed = false;
      }));
      on(document, 'visibilitychange', () => this.sync());
      on(this.motion, 'change', event => {
        this.reduced = event.matches;
        if (this.reduced) { this.auto = false; this.videoPaused = true; }
        this.sync();
      });
      on(document, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.contains(event.target));
        if (index >= 0) this.select(index, true);
      });
      this.videos.forEach((video, index) => {
        on(video, 'playing', () => {
          if (index !== this.index || !this.canPlay()) video.pause();
          this.updateVideoControl();
        });
        on(video, 'pause', () => this.updateVideoControl());
        on(video, 'error', () => this.updateVideoControl());
      });
      this.videoToggle.hidden = false;
      this.querySelector('[data-navigation]').hidden = this.slides.length < 2;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting && entries[0].intersectionRatio > .05;
          this.sync();
        }, {threshold:[0,.05,.25]});
        this.observer.observe(this);
      } else { this.inView = true; }
      this.sync();
      let previous = performance.now();
      const tick = now => {
        const delta = Math.min(now - previous, 250);
        previous = now;
        if (this.canRotate()) {
          this.elapsed += delta;
          if (this.elapsed >= this.interval) this.select((this.index + 1) % this.slides.length);
        }
        this.style.setProperty('--progress', this.auto ? Math.min(this.elapsed / this.interval,1) : 1);
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    canPlay() { return this.inView && !document.hidden && !this.videoPaused; }
    canRotate() {
      return this.auto && this.slides.length > 1 && this.inView && !document.hidden && !this.hovering && (!this.focused || this.rotationFocusAllowed);
    }
    select(index, manual = false) {
      if (manual) { this.auto = false; this.rotationFocusAllowed = false; }
      this.index = index;
      this.elapsed = 0;
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', String(!active));
        this.selectors[i].setAttribute('aria-pressed', String(active));
      });
      if (manual) this.status.textContent = this.slides[index].dataset.name;
      this.sync();
    }
    sync() {
      this.videos.forEach((video, index) => {
        if (index !== this.index || !this.canPlay()) { video.pause(); return; }
        if (!video.getAttribute('src')) {
          const src = innerWidth < 768 ? video.dataset.mobileSrc || video.dataset.src : video.dataset.src;
          if (!src) return;
          video.src = src;
        }
        video.muted = true;
        if (video.paused && !video.dataset.playPending) {
          video.dataset.playPending = 'true';
          const attempt = video.play();
          if (attempt) attempt.then(() => {
            if (index !== this.index || !this.canPlay()) video.pause();
          }).catch(error => {
            if (error.name !== 'AbortError' && index === this.index && this.canPlay()) this.videoPaused = true;
          }).finally(() => { delete video.dataset.playPending; this.updateVideoControl(); });
        }
      });
      const rotationLabel = this.auto ? this.dataset.pauseRotation : this.dataset.startRotation;
      this.rotationToggle.setAttribute('aria-label', rotationLabel);
      this.rotationToggle.title = rotationLabel;
      this.querySelector('[data-rotation-icon]').textContent = this.auto ? 'Ⅱ' : '▶';
      this.updateVideoControl();
    }
    updateVideoControl() {
      const video = this.videos[this.index];
      const paused = this.videoPaused || !video || video.paused;
      const label = paused ? this.dataset.playVideo : this.dataset.pauseVideo;
      this.videoToggle.setAttribute('aria-label', label);
      this.querySelector('[data-video-label]').textContent = label;
      this.querySelector('[data-video-icon]').textContent = paused ? '▶' : 'Ⅱ';
      this.videoToggle.hidden = !video?.dataset.src || !!video.error;
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.videos?.forEach(video => video.pause());
      this.initialized = false;
    }
  });
})();
